import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import getHasSeenAlertNotificationKey from 'src/components/Toasts/AlertNotifications/utils/getHasSeenAlertNotificationKey';
import { NewNotificationProps, notify } from 'src/utils/notifications';
import { PosthogEvent } from 'src/providers/posthog/posthogConfig';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

interface AlertNotificationParams extends Omit<
	NewNotificationProps,
	'showUntilCancelled' | 'id' | 'type'
> {
	/**
	 * An id is required for alert notifications.
	 */
	id: string;
	/**
	 * Captures the passed event when the notification is shown.
	 */
	event?: PosthogEvent;
}

/**
 * Returns a wrapper around notify that handles some "alert" notification specific logic.
 * Should only be called on the client as it accesses localStorage.
 */
export default function useAlertNotify() {
	const pathname = usePathname();
	const { captureEvent } = usePostHogCapture();

	return useCallback(
		({ event, ...params }: AlertNotificationParams) => {
			const hasSeenAlertNotification = localStorage.getItem(
				getHasSeenAlertNotificationKey(params.id)
			);

			const shouldAlertOnCurrentPath =
				!params.pathToAlertOn || pathname === params.pathToAlertOn;

			if (hasSeenAlertNotification || !shouldAlertOnCurrentPath) {
				return;
			}

			notify({
				...params,
				type: 'alert',
				// Alert notifications handle their own timing / auto-dismiss, so we simply
				// tell the underlying notification system to keep showing until cancelled.
				showUntilCancelled: true,
			});

			if (event) {
				captureEvent(event);
			}
		},
		[pathname, captureEvent]
	);
}
